import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import SearchIcon from "@mui/icons-material/Search";
import { auth, db, storage } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import Swal from "sweetalert2";

export interface StockItem {
  id: string;
  itemCode: string;
  itemName: string;
  description: string;
  quantity: number;
  reorderLevel: number;
  unit: string;
  purchasePrice: number;
  sellingPrice: number;
  category: string;
  imageUrls?: string[];
}

const StockManagement: React.FC = () => {
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState<StockItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);
  const [formData, setFormData] = useState<Partial<StockItem>>({
    itemCode: "",
    itemName: "",
    description: "",
    quantity: 0,
    reorderLevel: 0,
    unit: "pcs",
    purchasePrice: 0,
    sellingPrice: 0,
    category: "",
    imageUrls: [],
  });

  useEffect(() => {
    fetchStockItems();
  }, []);

  const fetchStockItems = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const stockCollection = collection(
        db,
        "stockItems",
        user.uid,
        "userStockItems",
      );
      const querySnapshot = await getDocs(query(stockCollection));
      const items = querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as StockItem,
      );
      setStockItems(items);

      // Extract unique categories
      const uniqueCategories = Array.from(
        new Set(items.map((item) => item.category).filter(Boolean)),
      ).sort() as string[];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching stock items:", error);
      Swal.fire("Error", "Failed to load stock items", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item?: StockItem) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
      setImagePreviews(item.imageUrls || []);
    } else {
      setEditingItem(null);
      setFormData({
        itemCode: "",
        itemName: "",
        description: "",
        quantity: 0,
        reorderLevel: 0,
        unit: "pcs",
        purchasePrice: 0,
        sellingPrice: 0,
        category: "",
        imageUrls: [],
      });
      setImagePreviews([]);
    }
    setImageFiles([]);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingItem(null);
    setImageFiles([]);
    setImagePreviews([]);
  };

  const handleImagesSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setImageFiles([...imageFiles, ...newFiles]);

      // Create previews
      newFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveImage = (index: number) => {
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    const newFiles = imageFiles.filter((_, i) => i !== index);
    setImagePreviews(newPreviews);
    setImageFiles(newFiles);

    // Also update form data to remove the URL
    const newUrls = (formData.imageUrls || []).filter((_, i) => i !== index);
    setFormData({ ...formData, imageUrls: newUrls });
  };

  const handleInputChange = (field: keyof StockItem, value: any) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSaveItem = async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Validation
    if (
      !formData.itemCode ||
      !formData.itemName ||
      !formData.unit ||
      formData.quantity === undefined
    ) {
      Swal.fire(
        "Error",
        "Please fill in all required fields (Code, Name, Unit, Quantity)",
        "error",
      );
      return;
    }

    setUploading(true);
    try {
      let imageUrls = formData.imageUrls || [];

      // Upload new images
      if (imageFiles.length > 0) {
        const uploadPromises = imageFiles.map(async (imageFile) => {
          const fileExtension = imageFile.name.split(".").pop();
          const fileName = `stock_items/${user.uid}/${Date.now()}_${Math.random()}.${fileExtension}`;
          const storageRef = ref(storage, fileName);

          await uploadBytes(storageRef, imageFile);
          const downloadUrl = await getDownloadURL(storageRef);
          return downloadUrl;
        });

        const uploadedUrls = await Promise.all(uploadPromises);
        imageUrls = [...imageUrls, ...uploadedUrls];

        // Delete old images if editing
        if (editingItem?.imageUrls && editingItem.imageUrls.length > 0) {
          const oldUrls = editingItem.imageUrls.filter(
            (url) => !imageUrls.includes(url),
          );
          oldUrls.forEach(async (url) => {
            try {
              const oldImageRef = ref(storage, url);
              await deleteObject(oldImageRef);
            } catch (error) {
              console.warn("Could not delete old image:", error);
            }
          });
        }
      }

      const dataToSave = { ...formData, imageUrls };

      if (editingItem) {
        // Update existing item
        const itemDoc = doc(
          db,
          "stockItems",
          user.uid,
          "userStockItems",
          editingItem.id,
        );
        await updateDoc(itemDoc, dataToSave);
        Swal.fire("Success", "Stock item updated", "success");
      } else {
        // Create new item
        const stockCollection = collection(
          db,
          "stockItems",
          user.uid,
          "userStockItems",
        );
        await addDoc(stockCollection, dataToSave);
        Swal.fire("Success", "Stock item created", "success");
      }
      handleCloseModal();
      fetchStockItems();
    } catch (error) {
      console.error("Error saving stock item:", error);
      Swal.fire("Error", "Failed to save stock item", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmResult.isConfirmed) return;

    const user = auth.currentUser;
    if (!user) return;

    try {
      const itemDoc = doc(db, "stockItems", user.uid, "userStockItems", id);
      await deleteDoc(itemDoc);
      Swal.fire("Deleted!", "Stock item has been deleted.", "success");
      fetchStockItems();
    } catch (error) {
      console.error("Error deleting stock item:", error);
      Swal.fire("Error", "Failed to delete stock item", "error");
    }
  };

  const getStockStatus = (quantity: number, reorderLevel: number) => {
    if (quantity === 0) return "outOfStock";
    if (quantity <= reorderLevel) return "lowStock";
    return "normal";
  };

  const getStatusChip = (quantity: number, reorderLevel: number) => {
    const status = getStockStatus(quantity, reorderLevel);
    switch (status) {
      case "outOfStock":
        return <Chip label="Out of Stock" color="error" size="small" />;
      case "lowStock":
        return <Chip label="Low Stock" color="warning" size="small" />;
      default:
        return <Chip label="In Stock" color="success" size="small" />;
    }
  };

  const downloadCSVTemplate = () => {
    const link = document.createElement("a");
    link.href = "/stock_template.csv";
    link.download = "stock_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (csvText: string): Partial<StockItem>[] => {
    const lines = csvText.trim().split("\n");
    if (lines.length === 0) return [];

    const headers = lines[0].split(",").map((h) => h.trim());
    const items: Partial<StockItem>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      if (values.length !== headers.length) continue;

      const item: any = {};
      headers.forEach((header, index) => {
        const value = values[index];
        if (
          header === "quantity" ||
          header === "reorderLevel" ||
          header === "purchasePrice" ||
          header === "sellingPrice"
        ) {
          item[header] = parseFloat(value) || 0;
        } else {
          item[header] = value;
        }
      });
      items.push(item);
    }

    return items;
  };

  const handleCSVImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csvText = e.target?.result as string;
        const items = parseCSV(csvText);

        if (items.length === 0) {
          Swal.fire("Error", "No valid items found in CSV", "error");
          return;
        }

        const user = auth.currentUser;
        if (!user) return;

        setUploading(true);
        const stockCollection = collection(
          db,
          "stockItems",
          user.uid,
          "userStockItems",
        );
        let importedCount = 0;
        let skippedCount = 0;

        for (const item of items) {
          if (!item.itemCode || !item.itemName || !item.unit) {
            skippedCount++;
            continue;
          }

          try {
            await addDoc(stockCollection, {
              ...item,
              imageUrls: [],
            });
            importedCount++;
          } catch (error) {
            console.error("Error importing item:", error);
            skippedCount++;
          }
        }

        setUploading(false);
        Swal.fire(
          "Import Complete",
          `Imported: ${importedCount}, Skipped: ${skippedCount}`,
          "success",
        );
        fetchStockItems();
      } catch (error) {
        console.error("Error parsing CSV:", error);
        setUploading(false);
        Swal.fire("Error", "Failed to parse CSV file", "error");
      }
    };

    reader.readAsText(file);
  };

  const getFilteredItems = () => {
    return stockItems.filter((item) => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        item.itemCode.toLowerCase().includes(searchLower) ||
        item.itemName.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;

      // Category filter
      if (categoryFilter && item.category !== categoryFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== "all") {
        const status = getStockStatus(item.quantity, item.reorderLevel);
        if (statusFilter !== status) {
          return false;
        }
      }

      return true;
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const filteredItems = getFilteredItems();

  return (
    <Box className="bg-white shadow-md rounded p-4">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Stock Management</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={downloadCSVTemplate}
            size="small"
          >
            Download CSV Template
          </Button>
          <Button
            component="label"
            variant="outlined"
            color="primary"
            startIcon={<UploadIcon />}
            disabled={uploading}
            size="small"
          >
            Import CSV
            <input
              hidden
              type="file"
              accept=".csv"
              onChange={handleCSVImport}
            />
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Add Stock Item
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Search */}
          <TextField
            placeholder="Search by code, name, or description..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {/* Status Filter */}
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(e, value) => value && setStatusFilter(value)}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="normal">In Stock</ToggleButton>
              <ToggleButton value="lowStock">Low Stock</ToggleButton>
              <ToggleButton value="outOfStock">Out of Stock</ToggleButton>
            </ToggleButtonGroup>

            {/* Category Filter */}
            {categories.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Filter by Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Results count */}
            <Typography
              variant="body2"
              sx={{ ml: "auto", color: "textSecondary" }}
            >
              {filteredItems.length} of {stockItems.length} items
            </Typography>
          </Box>
        </Box>
      </Paper>

      {filteredItems.length === 0 ? (
        <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
          {stockItems.length === 0
            ? "No stock items yet. Click 'Add Stock Item' to get started."
            : "No items match your filters. Try adjusting your search or filters."}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Reorder Level</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Purchase Price</TableCell>
                <TableCell align="right">Selling Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {item.imageUrls && item.imageUrls.length > 0 ? (
                      <Box sx={{ position: "relative" }}>
                        <Avatar
                          src={item.imageUrls[0]}
                          alt={item.itemName}
                          sx={{ width: 50, height: 50 }}
                        />
                        {item.imageUrls.length > 1 && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              right: 0,
                              backgroundColor: "#1976d2",
                              color: "white",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {item.imageUrls.length}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Avatar sx={{ width: 50, height: 50, bgcolor: "#ccc" }}>
                        No Image
                      </Avatar>
                    )}
                  </TableCell>
                  <TableCell>{item.itemCode}</TableCell>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell>{item.category || "-"}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{item.reorderLevel}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell align="right">
                    {item.purchasePrice.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {item.sellingPrice.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(item.quantity, item.reorderLevel)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(item)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Modal */}
      <Modal open={openModal} onClose={handleCloseModal} sx={{ zIndex: 1300 }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
            zIndex: 1301,
          }}
        >
          <Typography variant="h6" sx={{ mb: 3 }}>
            {editingItem ? "Edit Stock Item" : "Add Stock Item"}
          </Typography>

          {/* Image Upload Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Stock Item Photos
            </Typography>

            {/* Image Gallery */}
            {imagePreviews.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {imagePreviews.map((preview, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      paddingBottom: "100%",
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "1px solid #ddd",
                    }}
                  >
                    <img
                      src={preview}
                      alt={`Preview ${index}`}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.8)",
                        },
                        width: 24,
                        height: 24,
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Upload Area */}
            <Box
              component="label"
              sx={{
                display: "block",
                p: 2,
                border: "2px dashed #ccc",
                borderRadius: 1,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#fafafa",
                transition: "all 0.3s",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f0f7ff",
                },
              }}
            >
              <CameraAltIcon sx={{ fontSize: 32, color: "#ccc", mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Click to add more photos (multiple files allowed)
              </Typography>
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={handleImagesSelect}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Item Code *"
              value={formData.itemCode || ""}
              onChange={(e) => handleInputChange("itemCode", e.target.value)}
              fullWidth
              placeholder="e.g., SKU-001"
            />

            <TextField
              label="Item Name *"
              value={formData.itemName || ""}
              onChange={(e) => handleInputChange("itemName", e.target.value)}
              fullWidth
              placeholder="e.g., Steel Plate"
            />

            <TextField
              label="Description"
              value={formData.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Category"
              value={formData.category || ""}
              onChange={(e) => handleInputChange("category", e.target.value)}
              fullWidth
              placeholder="e.g., Raw Materials"
            />

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="Quantity *"
                type="number"
                value={formData.quantity || 0}
                onChange={(e) =>
                  handleInputChange("quantity", parseFloat(e.target.value))
                }
                fullWidth
              />
              <TextField
                label="Reorder Level"
                type="number"
                value={formData.reorderLevel || 0}
                onChange={(e) =>
                  handleInputChange("reorderLevel", parseFloat(e.target.value))
                }
                fullWidth
              />
            </Box>

            <TextField
              label="Unit *"
              value={formData.unit || ""}
              onChange={(e) => handleInputChange("unit", e.target.value)}
              fullWidth
              placeholder="e.g., pcs, kg, m, etc."
            />

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="Purchase Price"
                type="number"
                value={formData.purchasePrice || 0}
                onChange={(e) =>
                  handleInputChange("purchasePrice", parseFloat(e.target.value))
                }
                fullWidth
                inputProps={{ step: "0.01" }}
              />
              <TextField
                label="Selling Price"
                type="number"
                value={formData.sellingPrice || 0}
                onChange={(e) =>
                  handleInputChange("sellingPrice", parseFloat(e.target.value))
                }
                fullWidth
                inputProps={{ step: "0.01" }}
              />
            </Box>
          </Box>

          <Box
            sx={{ display: "flex", gap: 2, mt: 4, justifyContent: "flex-end" }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveItem}
              disabled={uploading}
            >
              {uploading ? (
                <CircularProgress size={24} />
              ) : editingItem ? (
                "Update Item"
              ) : (
                "Create Item"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default StockManagement;
